/**
 * @file Слой 1 - Лёгкая поверхность
 * @module membrane/src/layers/Layer1Surface
 * 
 * Первый слой мембраны - минималистичная поверхность, первое прикосновение.
 * Принципы: чистота, простота, отсутствие давления.
 * 
 * Запрещено: кнопки, меню, формы, CTA
 */

import { motion } from 'framer-motion';
import { BaseLayerProps } from '../types/membrane';
import { useEffect, useState } from 'react';
import Field1Canvas from '../visual/field1/Field1Canvas';

/**
 * Компонент Layer1Surface - лёгкая поверхность мембраны
 * 
 * Отображает минималистичный экран с фоном и одной фразой-приглашением.
 * Переход к следующему слою происходит естественно через время или внимание.
 */
export default function Layer1Surface({ onProceed }: BaseLayerProps) {
  const [hasInteracted, setHasInteracted] = useState(false);

  // Автоматический переход через время (мягкое приглашение)
  useEffect(() => {
    if (!hasInteracted && onProceed) {
      const timer = setTimeout(() => {
        // Не вызываем автоматически - ждём взаимодействия
        // Но создаём намёк на возможность
      }, 8000);

      return () => clearTimeout(timer);
    }
  }, [hasInteracted, onProceed]);

  // Реакция на внимание (движение мыши, скролл)
  useEffect(() => {
    const handleInteraction = () => {
      setHasInteracted(true);
      // Мягкий переход после небольшой задержки
      if (onProceed) {
        setTimeout(() => onProceed(), 2000);
      }
    };

    window.addEventListener('mousemove', handleInteraction, { once: true });
    window.addEventListener('scroll', handleInteraction, { once: true });
    window.addEventListener('touchstart', handleInteraction, { once: true });

    return () => {
      window.removeEventListener('mousemove', handleInteraction);
      window.removeEventListener('scroll', handleInteraction);
      window.removeEventListener('touchstart', handleInteraction);
    };
  }, [onProceed]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 2, ease: 'easeOut' }}
      className="absolute inset-0 w-full h-full"
    >
      {/* WebGL Canvas с очень светлым полем туманности */}
      <Field1Canvas className="absolute inset-0 w-full h-full" />
      
      {/* Основной контент */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 3, delay: 1, ease: 'easeOut' }}
        className="relative z-10 flex items-center justify-center h-full"
      >
        <motion.p
          className="text-2xl md:text-4xl font-light text-membrane-depth/80 leading-relaxed text-center px-8 max-w-2xl"
          animate={{
            opacity: [0.6, 1, 0.6],
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        >
          Есть то, что уже существует в будущем
        </motion.p>
      </motion.div>

      {/* Тонкий намёк на возможность движения (появляется через время) */}
      {hasInteracted && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.3 }}
          className="absolute bottom-12 left-1/2 transform -translate-x-1/2 z-10"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
            className="w-px h-12 bg-membrane-depth/20"
          />
        </motion.div>
      )}
    </motion.div>
  );
}

